import { useState } from 'react';
import { Search, Paperclip } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useConversations } from '@/lib/hooks/use-conversations';
import { useUser } from '@clerk/clerk-react';
import type { Conversation } from '@/lib/api/services/chat.service';

interface ChatSidebarProps {
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  otherUserOnlineStatuses: Record<string, boolean>;
}

export function ChatSidebar({
  selectedConversationId,
  onSelectConversation,
  otherUserOnlineStatuses,
}: ChatSidebarProps) {
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const { conversations, isLoading } = useConversations();

  // Get other participant info
  const getOtherParticipant = (conversation: Conversation) => {
    return conversation.participants.find((p) => p.user_id !== user?.id);
  };

  // Format last message time
  const formatLastMessageTime = (timestamp: string | null): string => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // For older messages, show date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Component to render conversation item with user name
  const ConversationItem = ({ conversation }: { conversation: Conversation }) => {
    const other = getOtherParticipant(conversation);
    // Use display_name and profile_picture_url from enriched conversation participant data
    const userName = other?.display_name || null;
    const profilePictureUrl = other?.profile_picture_url || null;
    const participant = conversation.participants.find((p) => p.user_id === user?.id);
    const unread = participant?.unread_count || 0;
    const isOnline = other?.user_id ? (otherUserOnlineStatuses[other.user_id] || false) : false;

    const initials = userName
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

    return (
      <button
        key={conversation.id}
        onClick={() => onSelectConversation(conversation.id)}
        className={`w-full p-4 flex items-start gap-3 transition border-b border-slate-100 hover:bg-slate-50 ${
          selectedConversationId === conversation.id
            ? 'bg-[#4669ed]/5 border-l-4 border-l-[#4669ed]'
            : ''
        }`}
      >
        <div className="relative flex-shrink-0">
          <Avatar className="h-12 w-12">
            {profilePictureUrl && (
              <AvatarImage src={profilePictureUrl} alt={userName || 'User'} />
            )}
            <AvatarFallback className="font-semibold bg-slate-200 text-slate-600">
              {initials}
            </AvatarFallback>
          </Avatar>
          {isOnline && (
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold truncate text-slate-900">
              {userName || (other?.role === 'genius' ? 'Freelancer' : 'Client')}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {conversation.last_message_at && (
                <span className="text-xs text-slate-400 whitespace-nowrap">
                  {formatLastMessageTime(conversation.last_message_at)}
                </span>
              )}
              {unread > 0 && (
                <span className="bg-[#4669ed] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {unread}
                </span>
              )}
            </div>
          </div>
          {conversation.last_message_preview ? (
            conversation.last_message_preview.startsWith('📎') ? (
              <div className="text-sm truncate mt-0.5 text-slate-400 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{conversation.last_message_preview.replace(/^📎\s*/, '')}</span>
              </div>
            ) : (
              <p className="text-sm truncate mt-0.5 text-slate-400">
                {conversation.last_message_preview}
              </p>
            )
          ) : (
            <p className="text-sm truncate mt-0.5 text-slate-400">
              No messages yet
            </p>
          )}
        </div>
      </button>
    );
  };

  // Filter conversations by search query
  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery.trim()) return true;
    // In a real implementation, you'd fetch user profile data
    // For now, we'll just filter by conversation ID or last message
    const query = searchQuery.toLowerCase();
    return (
      conv.id.toLowerCase().includes(query) ||
      conv.last_message_preview?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="w-80 border-r border-slate-200 bg-white flex flex-col h-full">
      <div className="p-4 border-b border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search conversations..."
            className="pl-10 rounded-full bg-slate-50 border-slate-200"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-slate-500">Loading conversations...</p>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-slate-500">No conversations found</p>
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <ConversationItem key={conversation.id} conversation={conversation} />
          ))
        )}
      </div>
    </div>
  );
}
